import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { usersAPI, authAPI, tenantsAPI } from '../../services/api';
import { formatDate } from '../../utils/dateFormat';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import './Admin.css';

interface User {
  id: string;
  email: string;
  username: string;
  full_name: string | null;
  role: string;
  tenant_id: number | null;
  // Normally a string from the API (t.name AS tenant_name). The operator's
  // cross-tenant list can carry an organization OBJECT instead; tenantLabel()
  // coerces either shape so it never reaches React as an object child (#31).
  tenant_name: string | { name?: string; subdomain?: string } | null;
  tenant?: { name?: string; subdomain?: string } | null;
  is_active: boolean;
  created_at: string;
  last_login: string | null;
}

interface Tenant {
  id: number;
  name: string;
  subdomain: string | null;
}

interface UserFormData {
  email: string;
  username: string;
  password: string;
  full_name: string;
  role: string;
  tenant_id: number | null;
  sendInvite: boolean;
}

// Coerce an axios error's `detail` into a renderable string. FastAPI 422
// validation errors return detail as an array of {type, loc, msg, ...} objects
// (e.g. editing a cross-tenant *.local seed user fails EmailStr); rendering that
// array/object directly as a React child throws minified React error #31.
const errMsg = (err: any, fallback: string): string => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d
      .map((e: any) => (typeof e === 'string' ? e : e?.msg || JSON.stringify(e)))
      .join('; ') || fallback;
  }
  if (d && typeof d === 'object') return d.msg || d.message || JSON.stringify(d);
  return fallback;
};

// Coerce a user's organization into a guaranteed renderable string. The list
// row may carry tenant_name as a string, OR an org object (operator cross-tenant
// view), OR null. Rendering an object directly throws React error #31.
const tenantLabel = (user: User): string => {
  const t: any = user.tenant_name ?? user.tenant;
  if (t == null) return '—';
  if (typeof t === 'string') return t;
  if (typeof t === 'object') return t.name ?? t.subdomain ?? '—';
  return String(t);
};

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showInactive, setShowInactive] = useState(false);
  const perPage = 20;

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    username: '',
    password: '',
    full_name: '',
    role: 'user',
    tenant_id: null,
    sendInvite: false,
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteUser, setDeleteUser] = useState<User | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Toast notification
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [sendingInvite, setSendingInvite] = useState<string | null>(null);
  const [confirmInviteUser, setConfirmInviteUser] = useState<User | null>(null);

  // Tenants list for dropdown
  const [tenants, setTenants] = useState<Tenant[]>([]);

  const fetchTenants = async () => {
    try {
      const response = await tenantsAPI.list();
      setTenants(response.data || []);
    } catch (err: any) {
      console.error('Failed to fetch tenants:', err);
      setToast({
        message: 'Failed to load organizations: ' + errMsg(err, 'Unknown error'),
        type: 'error'
      });
      setTimeout(() => setToast(null), 6000);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await usersAPI.list({
        page,
        per_page: perPage,
        search: search || undefined,
        show_deleted: showInactive || undefined,
      });
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (err: any) {
      setError(errMsg(err, 'Failed to load members'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, search, showInactive]);

  useEffect(() => {
    fetchTenants();
  }, []);

  const openCreateModal = () => {
    setModalMode('create');
    setEditingUser(null);
    setFormData({
      email: '',
      username: '',
      password: '',
      full_name: '',
      role: 'user',
      tenant_id: null,
      sendInvite: false,
    });
    setFormError(null);
    setShowModal(true);
  };

  const openEditModal = (user: User) => {
    setModalMode('edit');
    setEditingUser(user);
    setFormData({
      email: user.email,
      username: user.username,
      password: '',
      full_name: user.full_name || '',
      role: user.role,
      tenant_id: user.tenant_id,
      sendInvite: false,
    });
    setFormError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Re-entrancy guard: the disabled={saving} button only blocks clicks after
    // the next render, so a second submit (e.g. double Enter) can slip through
    // before saving flips. Bail synchronously to prevent a duplicate create.
    if (saving) return;
    setFormError(null);
    setSaving(true);

    try {
      if (modalMode === 'create') {
        // If sendInvite is NOT checked, password is required
        if (!formData.sendInvite && !formData.password) {
          setFormError('Password is required unless sending an invitation');
          setSaving(false);
          return;
        }
        // Create user with optional tenant_id
        // When sending invite, use a random temporary password
        const createData: any = {
          email: formData.email,
          username: formData.username,
          password: formData.sendInvite
            ? Math.random().toString(36).slice(-12) + 'A1!' // Temporary password
            : formData.password,
          full_name: formData.full_name || undefined,
          role: formData.role,
        };
        if (formData.tenant_id) {
          createData.tenant_id = formData.tenant_id;
        }
        const response = await usersAPI.create(createData);

        // If sendInvite is checked, send invitation email after creating user
        if (formData.sendInvite && response.data?.id) {
          try {
            await authAPI.sendInvite(response.data.id);
            setToast({ message: `Member created and invitation sent to ${formData.email}`, type: 'success' });
            setTimeout(() => setToast(null), 4000);
          } catch (inviteErr: any) {
            setToast({
              message: `Member created but failed to send invitation: ${errMsg(inviteErr, 'Unknown error')}`,
              type: 'error'
            });
            setTimeout(() => setToast(null), 6000);
          }
        }
      } else if (editingUser) {
        await usersAPI.update(editingUser.id, {
          email: formData.email,
          username: formData.username,
          full_name: formData.full_name || undefined,
          role: formData.role,
          tenant_id: formData.tenant_id ?? 0,  // 0 = clear tenant
        });
        // Update password separately if provided
        if (formData.password) {
          await usersAPI.changePassword(editingUser.id, formData.password);
        }
      }
      closeModal();
      fetchUsers();
    } catch (err: any) {
      setFormError(errMsg(err, 'Failed to save member'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    setDeleting(true);

    try {
      await usersAPI.delete(deleteUser.id);
      setDeleteUser(null);
      fetchUsers();
    } catch (err: any) {
      setError(errMsg(err, 'Failed to remove member'));
    } finally {
      setDeleting(false);
    }
  };

  const toggleUserStatus = async (user: User) => {
    try {
      await usersAPI.update(user.id, { is_active: !user.is_active });
      fetchUsers();
    } catch (err: any) {
      setError(errMsg(err, 'Failed to update member'));
    }
  };

  const sendInvite = async (user: User) => {
    setSendingInvite(user.id);
    try {
      await authAPI.sendInvite(user.id);
      setToast({ message: `Invitation sent to ${user.email}`, type: 'success' });
      setTimeout(() => setToast(null), 4000);
    } catch (err: any) {
      setToast({
        message: errMsg(err, 'Failed to send invitation'),
        type: 'error'
      });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setSendingInvite(null);
    }
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'admin': return 'badge-admin';
      case 'user': return 'badge-user';
      default: return 'badge-viewer';
    }
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="admin-users-page">
      <div className="admin-page-header">
        <h1>Members</h1>
        <button className="btn-primary" onClick={openCreateModal}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          Add Member
        </button>
      </div>

      {error && (
        <div className="error-message">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M8 4V9M8 11V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          {error}
        </div>
      )}

      <div className="admin-toolbar">
        <div className="search-input-wrapper">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M11 11L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            className="search-input"
            placeholder="Search members..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => { setShowInactive(e.target.checked); setPage(1); }}
          />
          Show inactive
        </label>
      </div>

      <div className="admin-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="admin-loading-spinner" />
          </div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="18" r="8" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 42C12 34.268 17.268 28 24 28C30.732 28 36 34.268 36 42" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <h3>No members found</h3>
            <p>{search ? 'Try a different search term' : 'Add your first member to get started'}</p>
          </div>
        ) : (
          <>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Organization</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="table-cell-primary">{user.full_name || user.username}</div>
                      <div className="table-cell-secondary">{user.email}</div>
                    </td>
                    <td className="table-cell-secondary">
                      {tenantLabel(user)}
                    </td>
                    <td>
                      <span className={`table-cell-badge ${getRoleBadgeClass(user.role)}`}>
                        {user.role === 'user' ? 'member' : user.role}
                      </span>
                    </td>
                    <td>
                      <span className={`table-cell-badge ${user.is_active ? 'badge-active' : 'badge-inactive'}`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="table-cell-secondary">
                      {formatDate(user.last_login)}
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="action-btn"
                          onClick={() => setConfirmInviteUser(user)}
                          title="Send invitation email"
                          disabled={sendingInvite === user.id}
                        >
                          {sendingInvite === user.id ? (
                            <div className="admin-loading-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M14 2L7 9M14 2L9.5 14L7 9M14 2L2 6.5L7 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </button>
                        <button
                          className="action-btn"
                          onClick={() => openEditModal(user)}
                          title="Edit member"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M11.5 2.5L13.5 4.5L5 13H3V11L11.5 2.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        <button
                          className="action-btn"
                          onClick={() => toggleUserStatus(user)}
                          title={user.is_active ? 'Deactivate member' : 'Activate member'}
                        >
                          {user.is_active ? (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
                              <path d="M5 8H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
                              <path d="M8 5V11M5 8H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                          )}
                        </button>
                        <button
                          className="action-btn action-btn--danger"
                          onClick={() => setDeleteUser(user)}
                          title="Remove member"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M3 4H13M6 4V3C6 2.44772 6.44772 2 7 2H9C9.55228 2 10 2.44772 10 3V4M12 4V13C12 13.5523 11.5523 14 11 14H5C4.44772 14 4 13.5523 4 13V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="pagination">
                <div className="pagination-info">
                  Showing {(page - 1) * perPage + 1} to {Math.min(page * perPage, total)} of {total} members
                </div>
                <div className="pagination-buttons">
                  <button
                    className="pagination-btn"
                    onClick={() => setPage(p => p - 1)}
                    disabled={page === 1}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M10 4L6 8L10 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button
                    className="pagination-btn"
                    onClick={() => setPage(p => p + 1)}
                    disabled={page === totalPages}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Modal — portaled to <body> so the overlay escapes the
          app-layout stacking context and its z-1000 competes at the document
          root, above the z-40 LiquidGlassHeader (same pattern as ReactionEditor). */}
      {showModal && createPortal(
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modalMode === 'create' ? 'Add Member' : 'Edit Member'}</h3>
              <button className="modal-close" onClick={closeModal}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {formError && (
                  <div className="error-message">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M8 4V9M8 11V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    {formError}
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="John Doe"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    autoComplete="off"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Username *</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="johndoe"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                    autoComplete="off"
                  />
                </div>

                {/* Password field - hidden when send invite is checked */}
                {!(modalMode === 'create' && formData.sendInvite) && (
                  <div className="form-group">
                    <label className="form-label">
                      Password {modalMode === 'create' ? '*' : '(leave blank to keep current)'}
                    </label>
                    {/* autoComplete="new-password" tells the browser this is a NEW password
                        being set, so it won't autofill the admin's saved login password. */}
                    <input
                      type="password"
                      className="form-input"
                      placeholder={modalMode === 'create' ? 'Enter password' : 'Enter new password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required={modalMode === 'create' && !formData.sendInvite}
                      autoComplete="new-password"
                    />
                  </div>
                )}

                {/* Send invite checkbox - only in create mode */}
                {modalMode === 'create' && (
                  <div className="form-group">
                    <label className="form-checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.sendInvite}
                        onChange={(e) => setFormData({ ...formData, sendInvite: e.target.checked, password: '' })}
                      />
                      <span className="form-checkbox-text">
                        Send invitation email (they will set their own password)
                      </span>
                    </label>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select
                    className="form-select"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="user">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                {/* Tenant dropdown — always shown so "platform user" is selectable */}
                <div className="form-group">
                  <label className="form-label">Organization</label>
                  <select
                    className="form-select"
                    value={formData.tenant_id || ''}
                    onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value ? Number(e.target.value) : null })}
                  >
                    <option value="">No organization (platform user)</option>
                    {tenants.map((tenant) => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name}{tenant.subdomain ? ` (${tenant.subdomain})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : modalMode === 'create' ? 'Create Member' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal — portaled to <body> for the same reason. */}
      {deleteUser && createPortal(
        <div className="modal-overlay" onClick={() => setDeleteUser(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Remove Member</h3>
              <button className="modal-close" onClick={() => setDeleteUser(null)}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to remove <strong>{deleteUser.full_name || deleteUser.email}</strong>? Their account will be deactivated and they will no longer be able to log in.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setDeleteUser(null)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                style={{ background: '#dc2626' }}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Removing...' : 'Remove Member'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Invitation Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!confirmInviteUser}
        onClose={() => setConfirmInviteUser(null)}
        onConfirm={() => {
          if (confirmInviteUser) {
            sendInvite(confirmInviteUser);
            setConfirmInviteUser(null);
          }
        }}
        title="Send Invitation Email"
        description={`Send an invitation email to ${confirmInviteUser?.email}? They will receive a link to set their password and access the platform.`}
        confirmLabel="Send Invite"
        confirmColor="blue"
      />

      {/* Toast Notification */}
      {toast && (
        <div
          className={`toast toast--${toast.type}`}
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            padding: '12px 20px',
            borderRadius: 8,
            background: toast.type === 'success' ? '#059669' : '#dc2626',
            color: 'white',
            fontWeight: 500,
            fontSize: 14,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1001,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            animation: 'slideUp 0.2s ease',
          }}
        >
          {toast.type === 'success' ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 8L6.5 11.5L13 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M8 4V9M8 11V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          )}
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default UsersPage;
